<div class="keys">

  @if (isOrganization) {
    <a class="keys-back" routerLink="/main/dashboard"><i class="fa fa-angle-left" aria-hidden="true"></i> Projects</a>

    <header class="keys-header">
      <p class="keys-eyebrow">Organization</p>
      <h1 class="keys-title" data-testid="api-keys-title">Organization API Keys</h1>
      <p class="keys-subtitle">
        Read-only keys for the errors and traces of <strong>every project</strong> in your organization — including
        projects you add later. For scripts, CI or an AI agent like Claude. A key can’t change anything.
      </p>
    </header>
  } @else {
    <a class="keys-back" routerLink="/main/project/{{ projectId }}"><i class="fa fa-angle-left" aria-hidden="true"></i> Project</a>

    <header class="keys-header">
      <p class="keys-eyebrow" data-testid="api-keys-project-name">{{ projectName() }}</p>
      <h1 class="keys-title" data-testid="api-keys-title">API Keys</h1>
      <p class="keys-subtitle">
        Read-only keys for this project’s errors and traces — for scripts, CI or an AI agent like Claude.
        A key only sees this project and can’t change anything.
        Need all projects? Use an <a routerLink="/main/api-keys" data-testid="api-keys-org-link">organization key</a>.
      </p>
    </header>
  }

  @if (loading()) {
    <div class="k-card k-card--skeleton" data-testid="api-keys-loading">
      <div class="sk sk--title"></div>
      <div class="sk"></div>
      <div class="sk sk--short"></div>
    </div>
  } @else if (loadError()) {
    <div class="k-card k-empty" data-testid="api-keys-load-error">
      <i class="fa fa-exclamation-triangle" aria-hidden="true"></i>
      <p>{{ loadErrorMessage() || 'Couldn’t load the API keys for this ' + (isOrganization ? 'organization' : 'project') + '.' }}</p>
    </div>
  } @else {

    <!-- Create -->
    <section class="k-card">
      <h2 class="k-card__title">Create a key</h2>
      <form class="k-create" (submit)="$event.preventDefault(); create()">
        <input class="k-input" type="text" maxlength="255" placeholder="e.g. Claude, CI pipeline"
          aria-label="Key name" data-testid="api-key-name"
          [value]="newKeyName()" (input)="newKeyName.set($any($event.target).value)">
        <button class="pw-btn pw-btn--primary" type="submit" [disabled]="!newKeyName().trim() || creating()"
          data-testid="api-key-create">
          <i class="fa" [class.fa-key]="!creating()" [class.fa-spinner]="creating()" [class.fa-spin]="creating()"
            aria-hidden="true"></i>
          Create key
        </button>
      </form>
    </section>

    <!-- The new key, shown once -->
    @if (createdKey(); as created) {
      <section class="k-card k-reveal" data-testid="api-key-created">
        <div class="k-reveal__head">
          <h2 class="k-card__title"><i class="fa fa-check-circle" aria-hidden="true"></i> “{{ created.name }}” is ready</h2>
          <button class="k-icon-btn" type="button" aria-label="Dismiss" (click)="createdKey.set(null)">
            <i class="fa fa-times" aria-hidden="true"></i>
          </button>
        </div>
        <p class="k-warn">Copy it now — for your security it won’t be shown again.</p>

        <div class="k-secret">
          <code data-testid="api-key-secret">{{ created.key }}</code>
          <button class="pw-btn pw-btn--ghost" type="button" (click)="copy(created.key, 'key')">
            <i class="fa" [class.fa-copy]="copied() !== 'key'" [class.fa-check]="copied() === 'key'" aria-hidden="true"></i>
            {{ copied() === 'key' ? 'Copied' : 'Copy' }}
          </button>
        </div>

        <label class="k-label">Hand it to Claude</label>
        <div class="k-snippet">
          <pre>{{ agentPrompt() }}</pre>
          <button class="pw-btn pw-btn--ghost k-snippet__copy" type="button" (click)="copy(agentPrompt(), 'prompt')">
            <i class="fa" [class.fa-copy]="copied() !== 'prompt'" [class.fa-check]="copied() === 'prompt'" aria-hidden="true"></i>
            {{ copied() === 'prompt' ? 'Copied' : 'Copy prompt' }}
          </button>
        </div>
      </section>
    }

    <!-- Existing keys -->
    <section class="k-card">
      <h2 class="k-card__title">Keys <span class="k-count">{{ keys().length }}</span></h2>

      @if (keys().length === 0) {
        <p class="k-hint">No keys yet. Create one above to give a script or agent read access.</p>
      } @else {
        <ul class="k-list" data-testid="api-key-list">
          @for (key of keys(); track key.uuid) {
            <li class="k-row">
              <div class="k-row__main">
                <span class="k-row__name">{{ key.name }}</span>
                <code class="k-row__prefix">{{ key.key_prefix }}…</code>
              </div>
              <div class="k-row__meta">
                <span>Created {{ key.created_at | date:'mediumDate' }} by {{ key.created_by }}</span>
                <span>{{ key.last_used_at ? 'Last used ' + (key.last_used_at | date:'medium') : 'Never used' }}</span>
              </div>
              <div class="k-row__actions">
                @if (confirmingRevoke() === key.uuid) {
                  <span class="k-row__confirm">Revoke?</span>
                  <button class="pw-btn pw-btn--danger pw-btn--sm" type="button" [disabled]="revoking() === key.uuid"
                    (click)="revoke(key)">Yes, revoke</button>
                  <button class="pw-btn pw-btn--ghost pw-btn--sm" type="button" (click)="confirmingRevoke.set(null)">Cancel</button>
                } @else {
                  <button class="pw-btn pw-btn--danger-outline pw-btn--sm" type="button"
                    (click)="confirmingRevoke.set(key.uuid)">Revoke</button>
                }
              </div>
            </li>
          }
        </ul>
      }
    </section>

    <!-- Usage -->
    <section class="k-card">
      <h2 class="k-card__title">Using a key</h2>
      <p class="k-hint">
        Send it as <code>Authorization: Bearer &lt;key&gt;</code> (or <code>X-Api-Key</code>). All endpoints are
        <code>GET</code>.
        @if (isOrganization) {
          First list the projects the key can read — each has a <code>uuid</code>:
        } @else {
          They live under:
        }
      </p>
      @if (isOrganization) {
        <div class="k-snippet">
          <pre data-testid="api-keys-projects-url">{{ projectsUrl }}</pre>
        </div>
        <p class="k-hint">Then use the per-project endpoints below, replacing <code>{{ '{' }}project_uuid{{ '}' }}</code>:</p>
      }
      <div class="k-snippet">
        <pre data-testid="api-keys-base-url">{{ apiBase() }}</pre>
      </div>

      <table class="k-endpoints">
        <tbody>
          @for (endpoint of endpoints; track endpoint.path) {
            <tr>
              <td><code>/{{ endpoint.path }}</code></td>
              <td>{{ endpoint.description }}</td>
            </tr>
          }
        </tbody>
      </table>

      <label class="k-label">Example</label>
      <div class="k-snippet">
        <pre>{{ curlExample() }}</pre>
        <button class="pw-btn pw-btn--ghost k-snippet__copy" type="button" (click)="copy(curlExample(), 'curl')">
          <i class="fa" [class.fa-copy]="copied() !== 'curl'" [class.fa-check]="copied() === 'curl'" aria-hidden="true"></i>
          {{ copied() === 'curl' ? 'Copied' : 'Copy' }}
        </button>
      </div>
    </section>
  }
</div>
